import { useCallback, useState, useEffect, useMemo } from 'react'
import { css } from '@emotion/react'
import CircularProgress from '@mui/material/CircularProgress'
import Collapse from '@mui/material/Collapse'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemText from '@mui/material/ListItemText'
import Typography from '@mui/material/Typography'
import debouncePromise from 'awesome-debounce-promise'
import { Paper, CvsTextField } from '@components'
import { t } from '@lingui/core/macro'

const container = css`
  position: relative;
  flex-grow: 1;
  margin-top: 16;
  margin-bottom: 8;
`

const absolute = css`
  position: absolute;
  z-index: 10;
`

const backgroundWhite = css`
  background-color: white;
  max-height: 200px;
  overflow-y: scroll;
`

export function CvsAutoCompleteAsync({
  selected,
  isMulti,
  onSelected,
  disabled,
  placeholder,
  label,
  options,
  loadOptions,
  ...rest
}) {
  const [searchTerms, setSearchTerms] = useState('')
  const [showList, setShowList] = useState(false)
  const [loading, setLoading] = useState(false)
  const [hasFocus, setHasFocus] = useState(false)
  const [defaultOptions, setDefaultOptions] = useState(options)

  const loadOptionsDebounced = useCallback(debouncePromise(loadOptions, 400), [loadOptions])

  const selectedObject = useMemo(() => {
    return defaultOptions.find(o => o.value === selected)
  }, [selected, defaultOptions])

  useEffect(() => {
    selectedObject && setDefaultOptions(options)
  }, [options, selectedObject])

  useEffect(() => {
    searchTerms.length ? setLoading(true) : setLoading(false)

    if (searchTerms.length) {
      loadOptionsDebounced(searchTerms)
        .then(() => setLoading(false))
        .catch(() => setLoading(false))
    }
  }, [searchTerms])

  return (
    <div css={container}>
      <CvsTextField
        disabled={disabled}
        label={label}
        value={hasFocus ? searchTerms : selectedObject ? selectedObject.label : ''}
        onChange={e => setSearchTerms(e.target.value)}
        placeholder={placeholder || 'Search...'}
        onBlur={() => {
          setHasFocus(false)
          setShowList(false)
          setSearchTerms('')
        }}
        onFocus={() => {
          setHasFocus(true)
          setShowList(true)
        }}
        {...rest}
      />
      <Collapse css={absolute} in={showList}>
        <Paper css={backgroundWhite}>
          <List dense>
            {options.length && !loading ? (
              options.map(({ label, value }) => (
                <ListItem
                  button
                  onClick={() => {
                    setDefaultOptions(options)
                    onSelected(isMulti ? [...selected, value] : value)
                  }}
                  key={value}
                >
                  <ListItemText>
                    <Typography>{label}</Typography>
                  </ListItemText>
                </ListItem>
              ))
            ) : loading ? (
              <ListItem>
                <CircularProgress />
              </ListItem>
            ) : (
              !options.length && (
                <ListItem>
                  <ListItemText>{t`No options`}</ListItemText>
                </ListItem>
              )
            )}
          </List>
        </Paper>
      </Collapse>
    </div>
  )
}
